// Контроллер воспроизведения голосовых (SPEC §4.2 «Голос», FR-VOICE). ОДИН скрытый HTMLAudioElement
// на ленту: старт нового сообщения останавливает предыдущее (как в Telegram). Источник — same-origin
// blob-прокси `/media/attachments/:id/blob` (ACL на сервере). Это медиа-элемент браузера, как <img>
// у вложений, — НЕ сетевой примитив app-ui инв.2 (fetch/WS запрещены страж-тестом, media-элементы нет).
//
// Известный квирк: webm/opus от MediaRecorder не несёт длительность в заголовке — audio.duration
// даёт Infinity (Chromium). Тогда обмер: seek в «бесконечность» → durationchange отдаёт реальную
// длительность → откат в 0 (стандартный приём). Обмеренная длительность подставляется в отображение
// вместо серверного 0 (история до фикса durationMs хранит 0).

import { useCallback, useEffect, useRef, useState } from 'react'
import type { Message, MessageId } from '@tele-chat/shared-contracts'
import type { VoicePlaybackProps } from '@tele-chat/design-system'

/** Цикл скоростей по тапу на «1×» (как в Telegram). */
const SPEEDS = [1, 1.5, 2] as const

/** Псевдо-время для обмера Infinity-duration (Chromium seek'ается к реальному концу). */
const PROBE_SEEK_SEC = 1e10

interface VoicePlaybackState {
  messageId: MessageId | null
  isPlaying: boolean
  currentTimeMs: number
  /** Обмеренная из метаданных длительность активного сообщения (мс); null — ещё неизвестна. */
  measuredDurationMs: number | null
  speed: number
}

const IDLE: VoicePlaybackState = {
  messageId: null,
  isPlaying: false,
  currentTimeMs: 0,
  measuredDurationMs: null,
  speed: 1,
}

export interface VoicePlaybackApi {
  /** props MessageBubble.voicePlayback для конкретного голосового сообщения. */
  forMessage(message: Message): VoicePlaybackProps
  /** Эффективная длительность для отображения (обмер приоритетнее серверного 0). */
  displayDurationMs(message: Message): number
}

/**
 * Хук ленты: play/pause/seek/скорость + раскрытие расшифровки, одно активное сообщение.
 * `onError` — наружу (тост показывает потребитель).
 */
export function useVoicePlayback(onError: (message: string) => void): VoicePlaybackApi {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  /** Идёт обмер Infinity-duration: timeupdate игнорируется, по завершении — откат в 0 и play. */
  const probingRef = useRef(false)
  const stateRef = useRef<VoicePlaybackState>(IDLE)
  const [state, setStateRaw] = useState<VoicePlaybackState>(IDLE)
  const [transcriptOpen, setTranscriptOpen] = useState<ReadonlySet<MessageId>>(new Set())

  const setState = useCallback((next: VoicePlaybackState | ((prev: VoicePlaybackState) => VoicePlaybackState)): void => {
    setStateRaw((prev) => {
      const value = typeof next === 'function' ? next(prev) : next
      stateRef.current = value
      return value
    })
  }, [])

  const ensureAudio = useCallback((): HTMLAudioElement => {
    if (audioRef.current !== null) return audioRef.current
    const audio = document.createElement('audio')
    audio.preload = 'metadata'

    audio.addEventListener('timeupdate', () => {
      if (probingRef.current) return
      setState((prev) => (prev.messageId === null ? prev : { ...prev, currentTimeMs: Math.round(audio.currentTime * 1000) }))
    })
    audio.addEventListener('durationchange', () => {
      const d = audio.duration
      if (!Number.isFinite(d) || d <= 0) return
      setState((prev) => (prev.messageId === null ? prev : { ...prev, measuredDurationMs: Math.round(d * 1000) }))
      if (probingRef.current) {
        // обмер завершён: откат к началу и запуск (play уже был запрошен пользователем)
        probingRef.current = false
        audio.currentTime = 0
        void audio.play().catch(() => {})
      }
    })
    audio.addEventListener('loadedmetadata', () => {
      if (!Number.isFinite(audio.duration)) {
        // live-webm: длительности в заголовке нет — обмер seek'ом в бесконечность
        probingRef.current = true
        audio.currentTime = PROBE_SEEK_SEC
      }
    })
    audio.addEventListener('play', () => setState((prev) => ({ ...prev, isPlaying: true })))
    audio.addEventListener('pause', () => setState((prev) => ({ ...prev, isPlaying: false })))
    audio.addEventListener('ended', () => {
      setState((prev) => ({ ...prev, isPlaying: false, currentTimeMs: 0 }))
    })
    audio.addEventListener('error', () => {
      probingRef.current = false
      setState((prev) => ({ ...prev, isPlaying: false }))
      onErrorRef.current('Не удалось воспроизвести голосовое')
    })

    audioRef.current = audio
    return audio
  }, [setState])

  // onError через ref — колбэк потребителя (notify) не должен пересоздавать audio/слушатели.
  const onErrorRef = useRef(onError)
  onErrorRef.current = onError

  // Размонтирование ленты (уход из чата) — остановить и освободить.
  useEffect(() => {
    return () => {
      const audio = audioRef.current
      if (audio !== null) {
        audio.pause()
        audio.removeAttribute('src')
        audio.load()
        audioRef.current = null
      }
    }
  }, [])

  const playPause = useCallback(
    (message: Message): void => {
      const voice = message.voice
      if (voice === undefined) return
      const audio = ensureAudio()
      const current = stateRef.current
      if (current.messageId === message.id) {
        if (audio.paused) void audio.play().catch(() => onErrorRef.current('Не удалось воспроизвести голосовое'))
        else audio.pause()
        return
      }
      // другое сообщение: остановить прежнее, зарядить новое (скорость сохраняется на ленту)
      probingRef.current = false
      audio.pause()
      audio.src = `/media/attachments/${encodeURIComponent(voice.attachmentId)}/blob`
      audio.playbackRate = current.speed
      setState({
        messageId: message.id,
        isPlaying: false,
        currentTimeMs: 0,
        measuredDurationMs: null,
        speed: current.speed,
      })
      void audio.play().catch(() => onErrorRef.current('Не удалось воспроизвести голосовое'))
    },
    [ensureAudio, setState],
  )

  const seek = useCallback(
    (message: Message, timeMs: number): void => {
      const audio = audioRef.current
      if (audio === null || stateRef.current.messageId !== message.id || probingRef.current) return
      if (!Number.isFinite(timeMs) || timeMs < 0) return
      audio.currentTime = timeMs / 1000
      setState((prev) => ({ ...prev, currentTimeMs: Math.round(timeMs) }))
    },
    [setState],
  )

  const cycleSpeed = useCallback((): void => {
    setState((prev) => {
      const idx = SPEEDS.indexOf(prev.speed as (typeof SPEEDS)[number])
      const speed = SPEEDS[(idx + 1) % SPEEDS.length] ?? 1
      const audio = audioRef.current
      if (audio !== null) audio.playbackRate = speed
      return { ...prev, speed }
    })
  }, [setState])

  const toggleTranscript = useCallback((id: MessageId): void => {
    setTranscriptOpen((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const displayDurationMs = useCallback(
    (message: Message): number => {
      const serverMs = message.voice?.waveform.durationMs ?? 0
      if (serverMs > 0) return serverMs
      const s = state
      return s.messageId === message.id && s.measuredDurationMs !== null ? s.measuredDurationMs : 0
    },
    [state],
  )

  const forMessage = useCallback(
    (message: Message): VoicePlaybackProps => {
      const active = state.messageId === message.id
      return {
        isPlaying: active && state.isPlaying,
        currentTimeMs: active ? state.currentTimeMs : 0,
        speed: state.speed,
        transcriptOpen: transcriptOpen.has(message.id),
        onPlayPause: () => playPause(message),
        onSeek: (timeMs) => seek(message, timeMs),
        onSpeed: cycleSpeed,
        onToggleTranscript: () => toggleTranscript(message.id),
      }
    },
    [state, transcriptOpen, playPause, seek, cycleSpeed, toggleTranscript],
  )

  return { forMessage, displayDurationMs }
}
